import { VersionCompareRow } from 'components/v2/compiler/version-compare/VersionCompareRow';
import { EpdVersionHistoryData } from 'components/v2/compiler/version-history/utils/EpdVersionHistoryDataContext';
import React from 'react';
import { INDICATOR_GROUP_CODES } from 'services/api/constants';
import { YES_NO } from '../constants';
import LcaResultsGridAdditionalConstruction from '../grids/lca-results/LcaResultsGridAdditionalConstruction';
import LcaResultsGridAdditionalMandatory from '../grids/lca-results/LcaResultsGridAdditionalMandatory';
import LcaResultsGridAdditionalVoluntary from '../grids/lca-results/LcaResultsGridAdditionalVoluntary';
import LcaResultsGridCoreConstruction from '../grids/lca-results/LcaResultsGridCoreConstruction';
import LcaResultsGridOutputFlowConstruction from '../grids/lca-results/LcaResultsGridOutputFlowConstruction';
import LcaResultsGridResourceConstruction from '../grids/lca-results/LcaResultsGridResourceConstruction';
import LcaResultsGridWasteConstruction from '../grids/lca-results/LcaResultsGridWasteConstruction';
import { getAdditionalVoluntaryRows } from '../version-history/content/VersionHistoryLcaResultsSection';
import VersionHistoryExpandableGridWrapper from '../version-history/VersionHistoryExpandableGridWrapper';
import VersionHistorySummaryGrid from '../version-history/VersionHistorySummaryGrid';
import { LcaImportContextProvider } from '../grids/lca-results/util/LcaImportContext';

const CompareEnvironmentPerformanceSection: React.FC<{
  data: { left: EpdVersionHistoryData | null; right: EpdVersionHistoryData | null };
}> = ({ data }) => {
  return (
    <>
      <LcaImportContextProvider>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item, compareClassName }) => (
            <VersionHistoryExpandableGridWrapper
              title="Mandatory environmental performance indicators according to EN 15804"
              expandedModeOnly={true}
            >
              <LcaResultsGridCoreConstruction
                className={compareClassName}
                epdVersionId={item!.epdVersionId}
                rows={item!.lcaResults.lcaResults?.filter((x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.CORE15804)}
                isAdditionalDisclaimerVisible={item!.infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>
          )}
        </VersionCompareRow>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item, compareClassName }) => (
            <VersionHistoryExpandableGridWrapper
              title="Additional mandatory environmental performance indicators"
              expandedModeOnly={true}
            >
              <LcaResultsGridAdditionalMandatory
                className={compareClassName}
                epdVersionId={item!.epdVersionId}
                rows={item!.lcaResults.lcaResults?.filter(
                  (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALMANDATORY
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>
          )}
        </VersionCompareRow>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item, compareClassName }) => (
            <VersionHistoryExpandableGridWrapper
              title="Additional voluntary environmental performance indicators according to EN 15804"
              expandedModeOnly={true}
            >
              <LcaResultsGridAdditionalConstruction
                className={compareClassName}
                epdVersionId={item!.epdVersionId}
                rows={item!.lcaResults.lcaResults?.filter(
                  (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALVOLUNTARY15804
                )}
                isAdditionalDisclaimerVisible={item!.infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>
          )}
        </VersionCompareRow>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item: data, compareClassName }) => {
            const additionalVoluntaryRows = getAdditionalVoluntaryRows(data);
            return (
              <VersionHistoryExpandableGridWrapper
                title="Additional voluntary environmental performance indicators"
                expandedModeOnly={true}
                maximizableContent={
                  <LcaResultsGridAdditionalVoluntary
                    className={compareClassName}
                    epdVersionId={data!.epdVersionId}
                    justificationForInclusion={data!.lcaSpecification?.justificationForInclusion}
                    disabled
                    preferEnabledAppearanceEvenWhenDisabled
                  />
                }
                additionalContent={
                  additionalVoluntaryRows.length > 0 && <VersionHistorySummaryGrid rows={additionalVoluntaryRows} />
                }
              />
            );
          }}
        </VersionCompareRow>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item: data, compareClassName }) => (
            <VersionHistoryExpandableGridWrapper
              title="Resource use indicators according to EN 15804"
              expandedModeOnly={true}
            >
              <LcaResultsGridResourceConstruction
                className={compareClassName}
                epdVersionId={data!.epdVersionId}
                rows={data!.lcaResults.lcaResults.filter(
                  (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.RESOURCE15804
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>
          )}
        </VersionCompareRow>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item: data, compareClassName }) => (
            <VersionHistoryExpandableGridWrapper title="Waste indicators according to EN 15804" expandedModeOnly>
              <LcaResultsGridWasteConstruction
                className={compareClassName}
                epdVersionId={data!.epdVersionId}
                rows={data!.lcaResults.lcaResults.filter(
                  (x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.WASTE15804
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>
          )}
        </VersionCompareRow>
        <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
          {({ item: data, compareClassName }) => (
            <VersionHistoryExpandableGridWrapper title="Output flow indicators according to EN 15804" expandedModeOnly>
              <LcaResultsGridOutputFlowConstruction
                className={compareClassName}
                epdVersionId={data!.epdVersionId}
                rows={data!.lcaResults.lcaResults.filter(
                  (x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.OUTPUTFLOW15804
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>
          )}
        </VersionCompareRow>
      </LcaImportContextProvider>
    </>
  );
};

export default CompareEnvironmentPerformanceSection;
